import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { waSettingsService, UpdateWaSettingsRequest } from '@services/wa-settings.service'
import { WaRateLimitConfig } from '@/types'

// User Settings
export function useWaSettings() {
  return useQuery({
    queryKey: ['waSettings'],
    queryFn: waSettingsService.getSettings,
  })
}

export function useUpdateWaSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateWaSettingsRequest) => waSettingsService.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waSettings'] })
    },
  })
}

export function useResetWaSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => waSettingsService.resetSettings(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waSettings'] })
    },
  })
}

// Account-specific Settings
export function useWaAccountSettings(accountId: number) {
  return useQuery({
    queryKey: ['waSettings', 'account', accountId],
    queryFn: () => waSettingsService.getAccountSettings(accountId),
    enabled: !!accountId,
  })
}

export function useUpdateWaAccountSettings(accountId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateWaSettingsRequest) =>
      waSettingsService.updateAccountSettings(accountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waSettings', 'account', accountId] })
    },
  })
}

export function useDeleteWaAccountSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (accountId: number) => waSettingsService.deleteAccountSettings(accountId),
    onSuccess: (_, accountId) => {
      queryClient.invalidateQueries({ queryKey: ['waSettings', 'account', accountId] })
    },
  })
}

// Auto-Reply
export function useWaAutoReplyConfig(accountId?: number) {
  return useQuery({
    queryKey: ['waSettings', 'autoReply', accountId],
    queryFn: () => waSettingsService.getAutoReplyConfig(accountId),
  })
}

export function useUpdateWaAutoReply() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      data,
      accountId,
    }: {
      data: { enabled: boolean; message?: string }
      accountId?: number
    }) => waSettingsService.updateAutoReply(data, accountId),
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: ['waSettings'] })
      if (accountId) {
        queryClient.invalidateQueries({ queryKey: ['waSettings', 'account', accountId] })
      }
      queryClient.invalidateQueries({ queryKey: ['waSettings', 'autoReply'] })
    },
  })
}

// Business Hours
export function useWaBusinessHoursCheck(accountId?: number) {
  return useQuery({
    queryKey: ['waSettings', 'businessHours', 'check', accountId],
    queryFn: () => waSettingsService.checkBusinessHours(accountId),
    refetchInterval: 60000, // Refresh every minute
  })
}

export function useUpdateWaBusinessHours() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      data,
      accountId,
    }: {
      data: { enabled: boolean; startHour: number; endHour: number; days: number[] }
      accountId?: number
    }) => waSettingsService.updateBusinessHours(data, accountId),
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: ['waSettings'] })
      if (accountId) {
        queryClient.invalidateQueries({ queryKey: ['waSettings', 'account', accountId] })
      }
      queryClient.invalidateQueries({ queryKey: ['waSettings', 'businessHours'] })
    },
  })
}

// Rate Limiting
export function useWaRateLimitCheck(accountId?: number) {
  return useQuery({
    queryKey: ['waSettings', 'rateLimit', 'check', accountId],
    queryFn: () => waSettingsService.checkRateLimit(accountId),
    refetchInterval: 30000, // Refresh every 30 seconds
  })
}

export function useUpdateWaRateLimits() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ config, accountId }: { config: WaRateLimitConfig; accountId?: number }) =>
      waSettingsService.updateRateLimits(config, accountId),
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: ['waSettings'] })
      if (accountId) {
        queryClient.invalidateQueries({ queryKey: ['waSettings', 'account', accountId] })
      }
      queryClient.invalidateQueries({ queryKey: ['waSettings', 'rateLimit'] })
    },
  })
}
